// @flow
import { createContext, FunctionalComponent, h } from 'preact';
import { useContext, useReducer } from 'preact/hooks';
import type {
  OrderDetailsResponseType,
  LenderDetailsType,
  EMIInfoType,
} from '../../api/controllers/checkoutController';

type CheckoutStateType = OrderDetailsResponseType & {
  orderDetailsCompletedInSeconds: number,
  orderTimeoutTimestampInSeconds?: number,
  selectedLenderDetails?: LenderDetailsType,
  selectedEmiPlanDetails?: EMIInfoType,
  userDetailsConfig?: {
    showMobileContainer: boolean,
    showEditIcon: boolean,
  },
  merchantData: {
    merchantURL?: string,
    merchantKey?: string,
    theme: any,
    merchantOrigin?: string,
  },
  closeIconConfig: {
    showCloseIcon: boolean,
    /** @type {boolean} to show cancellation popup or not on close */
    shouldShowCancellationPopup: boolean,
    /** @type {boolean} make popup appear and disappear*/
    showCancellationPopup: boolean,
  },
  closeWidgetOnBack: boolean,
  isInstacredOrigin: boolean,
  instaCredTxnId?: string,
  failureReason?: {
    title: string,
    message?: string,
    callbackData?: {
      payload: string,
      signature: string,
    },
  },
  successCallbackData?: {
    payload: string,
    signature: string,
  },
  isWhatsappConsentAccepted: boolean,
  showPreScreening: boolean,
};

const initialState: CheckoutStateType = {
  orderDetailsCompletedInSeconds: 0,
  userDetailsConfig: {
    showMobileContainer: false,
    showEditIcon: false,
  },
  merchantData: {},
  closeIconConfig: {
    showCloseIcon: true,
    shouldShowCancellationPopup: true,
    showCancellationPopup: false,
  },
  closeWidgetOnBack: false,
  isInstacredOrigin: false,
  isWhatsappConsentAccepted: true,
  showPreScreening: false, //TODO: Hardcoded for Arzoo
};

type Action = {
  type:
    | 'STORE_STATE'
    | 'RESET_STORE'
    | 'SEND_MESSAGE'
    | 'START_PAYMENT'
    | 'UPDATE_ORDER_TOKEN',
  payload?: CheckoutStateType,
};

type Dispatch = (action: Action) => void;

const CheckoutContext = createContext(initialState);

const CheckOutDispatchContext = createContext({
  checkoutDispatch: () => {},
});

const reducer = (
  state: CheckoutStateType,
  action: Action,
): CheckoutStateType => {
  switch (action.type) {
    case 'STORE_STATE':
      return {
        ...state,
        ...action.payload,
      };

    case 'START_PAYMENT': {
      const { merchantData } = action.payload || {};
      return {
        ...state,
        merchantData: {
          merchantURL: merchantData.merchantURL,
          merchantKey: merchantData.merchantKey,
          merchantOrigin: merchantData.merchantOrigin,
          theme: merchantData.theme,
        },
      };
    }
    case 'SEND_MESSAGE':
      const data = action.payload;

      if (state.merchantData.merchantOrigin && data) {
        const sendMessage = (messageData) => {
          return window.parent.postMessage(
            JSON.stringify(messageData),
            state.merchantData.merchantOrigin,
          );
        };

        switch (data.actionName) {
          case 'onTransactionComplete':
            sendMessage({
              actionName: data.actionName,
              payload: data.payload,
              signature: data.signature,
            });
            break;

          case 'listenOTPCredential':
            sendMessage({
              actionName: data.actionName,
            });
            break;
        }
      }

      return state;
    case 'RESET_STORE':
      return initialState;
    default:
      return state;
  }
};

const CheckoutProvider: FunctionalComponent = ({ children }) => {
  const [checkoutState, checkoutDispatch] = useReducer(reducer, initialState);

  const value = checkoutState;

  const dispatchValue = checkoutDispatch;

  return (
    <CheckoutContext.Provider value={value}>
      <CheckOutDispatchContext.Provider value={dispatchValue}>
        {children}
      </CheckOutDispatchContext.Provider>
    </CheckoutContext.Provider>
  );
};

const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);
  return context;
};

const useCheckoutDispatchContext = () => {
  const context = useContext(CheckOutDispatchContext);
  return context;
};

export { CheckoutProvider, useCheckoutContext, useCheckoutDispatchContext };



// WEBPACK FOOTER //
// ./storeProviders/checkoutWidgetStore/index.js